import { useEffect, useState } from 'react';
import DashboardCard from '../components/DashboardCard';
import Layout from '../components/Layout';
import { inventoryApi } from '../api';
import { FiSun, FiMoon } from 'react-icons/fi';
import ProductItem from './productitem';

const Dashboard = ({ currency, setCurrency }) => {
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedMode = localStorage.getItem('darkMode');
      if (savedMode !== null) return savedMode === 'true';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  
  const [stats, setStats] = useState({
    totalItems: 0,
    expiringSoon: 0,
    lowStock: 0,
    monthlySpend: 0
  });
  const [loading, setLoading] = useState(true);
  const [materials, setMaterials] = useState([]);
  const [showExpiringSoon, setShowExpiringSoon] = useState(false);
  const [showLowStock, setShowLowStock] = useState(false);
  const [showAllItems, setShowAllItems] = useState(false);
  const [previousStats, setPreviousStats] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [materialsResponse, alertsResponse] = await Promise.all([
          inventoryApi.getMaterials(),
          inventoryApi.getAlerts()
        ]);

        setMaterials(materialsResponse);

        const totalItems = materialsResponse.length;
        
        const expiringSoonItems = materialsResponse.filter(item => {
          const today = new Date();
          const expiryDate = item.expiry_date ? new Date(item.expiry_date) : null;
          const daysUntilExpiry = expiryDate ? Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24)) : null;
          return expiryDate && item.permissible_expiry_days && 
                 daysUntilExpiry > 0 && 
                 daysUntilExpiry <= item.permissible_expiry_days;
        });
        const expiringSoon = expiringSoonItems.length;

        const lowStockItems = materialsResponse.filter(item => {
          const currentQty = Number(item.current_quantity);
          const minQty = Number(item.min_quantity);
          return currentQty <= minQty;
        });
        const lowStock = lowStockItems.length;

        const monthlySpend = materialsResponse.reduce(
          (sum, item) => sum + (item.price_per_unit * item.current_quantity), 0
        );

        let changes = {
          totalItemsChange: 0,
          expiringSoonChange: 0,
          lowStockChange: 0,
          monthlySpendChange: 0
        };

        if (previousStats) {
          changes = {
            totalItemsChange: totalItems - previousStats.totalItems,
            expiringSoonChange: expiringSoon - previousStats.expiringSoon,
            lowStockChange: lowStock - previousStats.lowStock,
            monthlySpendChange: monthlySpend - previousStats.monthlySpendValue
          };
        }

        const totalItemsPercentage = materialsResponse.length > 0 ? 
          Math.min(100, Math.round((totalItems / (totalItems + 20)) * 100)) : 0;

        const expiringSoonPercentage = materialsResponse.length > 0 ? 
          Math.round((expiringSoon / materialsResponse.length) * 100) : 0;
          
        const lowStockPercentage = materialsResponse.length > 0 ? 
          Math.round((lowStock / materialsResponse.length) * 100) : 0;
          
        const monthlySpendPercentage = monthlySpend > 0 ? 
          Math.min(100, Math.round((monthlySpend / (monthlySpend * 1.3)) * 100)) : 0;

        setStats({
          totalItems,
          expiringSoon,
          lowStock,
          monthlySpend: monthlySpend.toLocaleString(currency.locale, {
            style: 'currency',
            currency: currency.code
          }),
          monthlySpendValue: monthlySpend,
          changes,
          percentages: {
            totalItems: totalItemsPercentage,
            expiringSoon: expiringSoonPercentage,
            lowStock: lowStockPercentage,
            monthlySpend: monthlySpendPercentage
          },
          suggestions: {
            totalItems: totalItems > 0 ? `Inventory health: ${totalItems} items tracked` : 'No items in inventory',
            expiringSoon: expiringSoon > 0 ? 
              `${expiringSoon} items need attention soon` : 
              'No items expiring soon',
            lowStock: lowStock > 0 ? 
              `${lowStock} items below minimum quantity` : 
              'All items well stocked',
            monthlySpend: monthlySpend > 0 ? 
              `Current monthly spend: ${monthlySpend.toLocaleString(currency.locale, {
                style: 'currency',
                currency: currency.code
              })}` : 
              'No spending recorded'
          }
        });

        setPreviousStats({
          totalItems,
          expiringSoon,
          lowStock,
          monthlySpendValue: monthlySpend
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currency]);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark');
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.body.classList.remove('dark');
      document.documentElement.classList.remove('dark');
      localStorage.setItem('darkMode', 'false');
    }
  }, [darkMode]);

  const getExpiringSoonItems = () => {
    return materials.filter(item => {
      const today = new Date();
      const expiryDate = item.expiry_date ? new Date(item.expiry_date) : null;
      const daysUntilExpiry = expiryDate ? Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24)) : null;
      return expiryDate && item.permissible_expiry_days && 
             daysUntilExpiry > 0 && 
             daysUntilExpiry <= item.permissible_expiry_days;
    });
  };

  const getLowStockItems = () => {
    return materials.filter(item => {
      const currentQty = Number(item.current_quantity);
      const minQty = Number(item.min_quantity);
      return currentQty <= minQty;
    });
  };

  const formatChange = (value) => {
    if (value > 0) return `+${value}`;
    if (value < 0) return `${value}`;
    return '';
  };

  return (
    <Layout currency={currency} setCurrency={setCurrency}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-primary">
            AI Dashboard
          </h2>
          <p className="text-secondary text-sm">
            Ready to help optimize your kitchen
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <button 
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? (
              <FiSun className="text-yellow-400" size={20} />
            ) : (
              <FiMoon className="text-gray-600" size={20} />
            )}
          </button>
          <button className="px-3 py-1.5 text-sm font-medium bg-indigo-50 dark:bg-indigo-900 dark:text-indigo-100 text-indigo-600 rounded-md hover:bg-indigo-100 dark:hover:bg-indigo-800 transition-colors">
            AI Insights
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <DashboardCard 
          title="Total Items" 
          value={loading ? '...' : stats.totalItems}
          change={loading ? '...' : formatChange(stats.changes?.totalItemsChange || 0)}
          changeText={loading ? 'Loading...' : stats.suggestions?.totalItems}
          percentage={loading ? 0 : stats.percentages?.totalItems || 0} 
          percentageText={loading ? '...' : `${stats.percentages?.totalItems || 0}%`} 
          onClick={() => setShowAllItems(!showAllItems)}
        />
        
        <DashboardCard 
          title="Expiring Soon" 
          value={loading ? '...' : stats.expiringSoon}
          change={loading ? '...' : formatChange(stats.changes?.expiringSoonChange || 0)}
          changeText={loading ? 'Loading...' : stats.suggestions?.expiringSoon}
          percentage={loading ? 0 : stats.percentages?.expiringSoon || 0} 
          percentageText={loading ? '...' : `${stats.percentages?.expiringSoon || 0}%`} 
          onClick={() => setShowExpiringSoon(!showExpiringSoon)}
        />
        
        <DashboardCard 
          title="Low Stock" 
          value={loading ? '...' : stats.lowStock}
          change={loading ? '...' : formatChange(stats.changes?.lowStockChange || 0)}
          changeText={loading ? 'Loading...' : stats.suggestions?.lowStock}
          percentage={loading ? 0 : stats.percentages?.lowStock || 0} 
          percentageText={loading ? '...' : `${stats.percentages?.lowStock || 0}%`} 
          onClick={() => setShowLowStock(!showLowStock)}
        />
        
        <DashboardCard 
          title="Monthly Spend" 
          value={loading ? '...' : stats.monthlySpend}
          change={loading ? '...' : stats.changes?.monthlySpendChange !== 0 ? 
            `${formatChange(Math.round((stats.changes?.monthlySpendChange || 0) / (stats.monthlySpendValue) * 100))}%` : 
            ''}
          changeText={loading ? 'Loading...' : stats.suggestions?.monthlySpend}
          percentage={loading ? 0 : stats.percentages?.monthlySpend || 0} 
          percentageText={loading ? '...' : `${stats.percentages?.monthlySpend || 0}%`} 
        />
      </div>

      {/* Expanded sections */}
      {showAllItems && (
        <div className="mt-6 bg-primary rounded-lg p-4 shadow border border-primary">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-primary">
              All Items ({materials.length})
            </h3>
            <button 
              onClick={() => setShowAllItems(false)}
              className="text-sm text-secondary hover:text-primary"
            >
              Close
            </button>
          </div>
          <div className="flex flex-wrap -mx-1">
            {materials.map((item) => (
              <ProductItem 
                key={item.id} 
                product={item} 
                currency={currency}
                onEdit={() => {}} 
                onDelete={() => {}}
              />
            ))}
          </div>
        </div>
      )}

      {showExpiringSoon && (
        <div className="mt-6 bg-primary rounded-lg p-4 shadow border border-primary">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-primary">
              Expiring Soon ({getExpiringSoonItems().length})
            </h3>
            <button 
              onClick={() => setShowExpiringSoon(false)}
              className="text-sm text-secondary hover:text-primary"
            >
              Close
            </button>
          </div>
          <div className="flex flex-wrap -mx-1">
            {getExpiringSoonItems().map((item) => (
              <ProductItem 
                key={item.id} 
                product={item} 
                currency={currency}
                onEdit={() => {}} 
                onDelete={() => {}}
              />
            ))}
          </div>
        </div>
      )}

      {showLowStock && (
        <div className="mt-6 bg-primary rounded-lg p-4 shadow border border-primary">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-primary">
              Low Stock ({getLowStockItems().length})
            </h3>
            <button 
              onClick={() => setShowLowStock(false)}
              className="text-sm text-secondary hover:text-primary"
            >
              Close
            </button>
          </div>
          <div className="flex flex-wrap -mx-1">
            {getLowStockItems().map((item) => (
              <ProductItem 
                key={item.id} 
                product={item} 
                currency={currency}
                onEdit={() => {}} 
                onDelete={() => {}}
              />
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Dashboard;